import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminGet, adminPost } from "../../lib/adminApi";
import { PageHeader, StatusBadge, LoadingRow, EmptyRow, ErrorBanner, OvergeslagenBanner, PaginationBar, formatDate, labelize } from "../../components/admin/AdminUI";
import { BERICHT_NAMEN, BERICHT_REDEN_NAMEN, KANAAL_NAMEN, noemNetjes } from "../../lib/namen";
import { pilotVerzendMelding, verzendenStaatAan } from "../../lib/verzendstand";

const STATUS_OPTIES = ["", "pending", "sending", "verzonden", "geopend", "gefaald", "overgeslagen", "bounced", "complained"];
const KANAAL_OPTIES = ["", "email", "sms", "whatsapp"];
const KANAAL_KLEUR = {
  email: "bg-blue-100 text-blue-800",
  sms: "bg-purple-100 text-purple-800",
  whatsapp: "bg-emerald-100 text-emerald-800",
};

function overdueTone(createdAt, status) {
  if (status !== "pending" && status !== "sending") return null;
  if (!createdAt) return null;
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const hours = diffMs / (1000 * 60 * 60);
  if (hours < 24) return { cls: "bg-emerald-100 text-emerald-800", label: "< 24u" };
  if (hours < 72) return { cls: "bg-amber-100 text-amber-800", label: `${Math.floor(hours / 24)}d` };
  if (hours < 168) return { cls: "bg-orange-100 text-orange-900", label: `${Math.floor(hours / 24)}d` };
  return { cls: "bg-rose-100 text-rose-800", label: `${Math.floor(hours / 24)}d` };
}

/**
 * "Resend gepland" — maar vertrekt het bericht ook echt? (§4.10)
 *
 * Tijdens de pilot gaat alleen mail weg van de soorten die op `MAIL_TOEGESTAAN` staan;
 * de rest wordt klaargezet en meteen overgeslagen. Deze pagina meldde tot nu toe
 * "Resend gepland" en "Bulk-resend klaar: X gelukt" — óók voor een bericht dat gegarandeerd
 * blijft staan. Dat leest als een geslaagde handeling terwijl er niets gebeurt, en dat is
 * precies hoe je een middag kwijtraakt aan zoeken waar de mail bleef.
 *
 * De lijst komt uit `GET /api/` (`mail_toegestaan`) en wordt hier NIET nagemaakt: een
 * tweede kopie in de frontend gaat gegarandeerd uit de pas lopen met de backend. Is de
 * lijst (nog) niet op te halen, dan valt de melding terug op de neutrale tekst — dan liever
 * niets beweren dan iets beweren dat misschien niet klopt.
 *
 * @param {string} type          het soort bericht
 * @param {string[]|null} toegestaan  `mail_toegestaan` uit GET /api/, of null als onbekend
 */
export function berichtVertrektEcht(type, toegestaan) {
  if (!Array.isArray(toegestaan)) return null; // onbekend
  return toegestaan.includes(type);
}

/** Wat er in het meldingsvenster komt te staan na "Opnieuw" (§4.10). */
export function resendMelding(type, toegestaan) {
  if (berichtVertrektEcht(type, toegestaan) === false) {
    return BERICHT_REDEN_NAMEN.niet_toegestaan_in_pilot;
  }
  return "Bericht opnieuw klaargezet";
}

/** Wat er na de bulkknop komt te staan (§4.10). */
export function bulkMelding({ gelukt, mislukt, blijftStaan }) {
  const delen = [`${gelukt} klaargezet`];
  if (mislukt > 0) delen.push(`${mislukt} mislukt`);
  if (blijftStaan > 0) {
    delen.push(`${blijftStaan} gaat tijdens de pilot niet de deur uit`);
  }
  return delen.join(", ");
}

export default function NotificatiesPage() {
  const [status, setStatus] = useState("");
  const [kanaal, setKanaal] = useState("");
  const [werkgever, setWerkgever] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;
  const visibleRef = useRef(true);
  const qc = useQueryClient();

  useEffect(() => {
    const onVis = () => { visibleRef.current = !document.hidden; };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const werkgeversQ = useQuery({
    queryKey: ["werkgevers", "for-notif-filter"],
    // 100 is het maximum dat de API toestaat (Query(le=100)). Met 200 gaf elke
    // aanroep een 422 en bleef de keuzelijst leeg — filteren op werkgever werkte
    // hier niet, zonder zichtbare fout.
    queryFn: () => adminGet("/api/admin/werkgevers", { limit: 100 }),
  });

  const params = { limit: LIMIT, offset };
  if (status) params.status = status;
  if (kanaal) params.kanaal = kanaal;
  if (werkgever) params.werkgever_id = werkgever;
  if (search) params.search = search;

  const q = useQuery({
    queryKey: ["notificaties", params],
    queryFn: () => adminGet("/api/admin/notificaties", params),
    keepPreviousData: true,
    refetchInterval: () => (visibleRef.current ? 30000 : false),
  });

  // Welke soorten mail tijdens de pilot echt de deur uit gaan. Uit `GET /api/`, zodat er
  // geen tweede kopie van die lijst in de frontend ontstaat (§4.10).
  const pilotQ = useQuery({
    queryKey: ["api-root", "mail-toegestaan"],
    queryFn: () => adminGet("/api/"),
    staleTime: 60 * 60 * 1000,
  });
  const mailToegestaan = pilotQ.data?.mail_toegestaan ?? null;
  // Staat het verzenden überhaupt aan? Zonder dit meldde deze pagina dat bepaalde
  // soorten "echt de deur uit" gaan terwijl `EMAIL_SENDER_ENABLED` uit staat en er dus
  // niets vertrok. Uit dezelfde aanvraag, want het staat in hetzelfde antwoord.
  const verzendenAan = verzendenStaatAan(pilotQ.data);

  const resendMut = useMutation({
    mutationFn: (id) => adminPost(`/api/admin/notificaties/${id}/resend`),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Opnieuw klaarzetten mislukt"),
  });

  const items = q.data?.items || q.data || [];
  const total = q.data?.total ?? items.length;

  const failed = useMemo(() => items.filter((n) => n.status === "gefaald"), [items]);

  function opnieuw(n) {
    resendMut.mutate(n.notificatie_id, {
      onSuccess: () => {
        // Niet "Resend gepland": bij een soort die tijdens de pilot niet mag, blijft het
        // bericht gewoon staan. Dan hoort dat er te staan.
        toast.success(resendMelding(n.type, mailToegestaan));
        qc.invalidateQueries({ queryKey: ["notificaties"] });
      },
    });
  }

  async function bulkResend() {
    if (!failed.length) return;
    if (!confirm(`${failed.length} mislukte berichten opnieuw klaarzetten?`)) return;
    let gelukt = 0;
    let mislukt = 0;
    let blijftStaan = 0;
    for (const n of failed) {
      try {
        await resendMut.mutateAsync(n.notificatie_id);
        gelukt++;
        if (berichtVertrektEcht(n.type, mailToegestaan) === false) blijftStaan++;
      } catch (e) {
        mislukt++;
      }
    }
    qc.invalidateQueries({ queryKey: ["notificaties"] });
    toast.success(bulkMelding({ gelukt, mislukt, blijftStaan }));
  }

  function toggleExpand(id) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div data-testid="notificaties-page">
      <PageHeader
        title="Notificaties"
        subtitle="Berichten verstuurd aan werknemers — vernieuwt automatisch elke 30 seconden"
        actions={
          failed.length > 0 ? (
            <button
              type="button"
              onClick={bulkResend}
              data-testid="btn-bulk-resend"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              Zet {failed.length} mislukte opnieuw klaar
            </button>
          ) : null
        }
      />
      {/* Zolang de pilot loopt vertrekt er alleen mail van de soorten op de lijst uit
          `GET /api/`. Dat hoort bovenaan te staan en niet pas in een toast na een klik.
          Wát er staat hangt af van de verzendstand: staat het verzenden uit, dan gaat er
          van die soorten óók niets weg en mag hier niet beweerd worden van wel. */}
      {Array.isArray(mailToegestaan) ? (
        <p data-testid="pilot-melding" className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {pilotVerzendMelding(
            verzendenAan,
            mailToegestaan.map((t) => noemNetjes(t, BERICHT_NAMEN)),
          )}
        </p>
      ) : null}
      <div className="mb-4 flex flex-wrap gap-2">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          aria-label="Status-filter" data-testid="notif-status-filter"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
        >
          {STATUS_OPTIES.map((s) => <option key={s || "_all"} value={s}>{s ? labelize(s, { pending: "In wachtrij", sending: "Wordt verzonden", verzonden: "Verzonden", geopend: "Geopend", gefaald: "Mislukt", overgeslagen: "Overgeslagen", bounced: "Onbestelbaar", complained: "Klacht ontvangen" }) : "Alle statussen"}</option>)}
        </select>
        <select
          value={kanaal}
          onChange={(e) => { setKanaal(e.target.value); setOffset(0); }}
          aria-label="Kanaal-filter" data-testid="notif-kanaal-filter"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
        >
          {KANAAL_OPTIES.map((k) => <option key={k || "_all"} value={k}>{k ? noemNetjes(k, KANAAL_NAMEN) : "Alle kanalen"}</option>)}
        </select>
        <select
          value={werkgever}
          onChange={(e) => { setWerkgever(e.target.value); setOffset(0); }}
          aria-label="Werkgever-filter" data-testid="notif-werkgever-filter"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
        >
          <option value="">Alle werkgevers</option>
          {(werkgeversQ.data?.items || []).map((w) => (
            <option key={w.werkgever_id} value={w.werkgever_id}>{w.bedrijfsnaam}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Zoek ontvanger of klantnummer"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
          aria-label="Zoek notificatie" data-testid="notif-search"
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
        />
      </div>
      <ErrorBanner error={q.error} />
      <OvergeslagenBanner
        aantal={q.data?.overgeslagen} enkelvoud="notificatie" meervoud="notificaties"
        testid="notificaties-overgeslagen"
      />
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm" data-testid="notificaties-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Tijdstip</th>
              <th className="px-4 py-2 text-left">Kanaal</th>
              <th className="px-4 py-2 text-left">Bericht</th>
              <th className="px-4 py-2 text-left">Ontvanger</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Wachttijd</th>
              <th className="px-4 py-2 text-right">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.isLoading ? <LoadingRow cols={7} /> :
              items.length === 0 ? <EmptyRow cols={7} /> :
              items.map((n) => {
                const overdue = overdueTone(n.created_at, n.status);
                const foutReden = n.fout_reden || n.error_message;
                const heeftFoutDetails = Boolean(foutReden);
                const isOpen = expanded.has(n.notificatie_id);
                return (
                  <React.Fragment key={n.notificatie_id}>
                    <tr data-testid={`notif-row-${n.notificatie_id}`} className={heeftFoutDetails ? "cursor-pointer hover:bg-slate-50" : ""} onClick={() => heeftFoutDetails && toggleExpand(n.notificatie_id)}>
                      <td className="px-4 py-2 text-xs text-slate-500">{formatDate(n.created_at || n.gepland_voor)}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${KANAAL_KLEUR[n.kanaal] || "bg-slate-100 text-slate-700"}`}>
                          {noemNetjes(n.kanaal, KANAAL_NAMEN)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs" title={n.type || ""}>{noemNetjes(n.type, BERICHT_NAMEN)}</td>
                      <td className="px-4 py-2">{n.ontvanger_email || n.ontvanger || "—"}</td>
                      <td className="px-4 py-2"><StatusBadge value={n.status} testid={`notif-status-${n.notificatie_id}`} /></td>
                      <td className="px-4 py-2">
                        {overdue ? (
                          <span className={`rounded px-2 py-0.5 text-xs font-medium ${overdue.cls}`}>{overdue.label}</span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                        {n.status === "gefaald" ? (
                          <button
                            type="button"
                            onClick={() => opnieuw(n)}
                            data-testid={`btn-resend-${n.notificatie_id}`}
                            title={
                              berichtVertrektEcht(n.type, mailToegestaan) === false
                                ? BERICHT_REDEN_NAMEN.niet_toegestaan_in_pilot
                                : undefined
                            }
                            className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                          >
                            Opnieuw
                          </button>
                        ) : heeftFoutDetails ? (
                          <span className="text-xs text-slate-400">{isOpen ? "▲" : "▼"}</span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </td>
                    </tr>
                    {isOpen && heeftFoutDetails ? (
                      <tr data-testid={`notif-detail-${n.notificatie_id}`}>
                        <td colSpan={7} className="bg-rose-50 px-4 py-3 text-xs text-rose-900">
                          <div className="font-medium">Foutdetails</div>
                          <pre className="mt-1 whitespace-pre-wrap break-words">{foutReden}</pre>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>
      <PaginationBar
        offset={offset} limit={LIMIT} total={total}
        onPrev={() => setOffset(Math.max(0, offset - LIMIT))}
        onNext={() => setOffset(offset + LIMIT)}
      />
    </div>
  );
}
